// Task Details Modal Component
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  X, Calendar, User, Flag, Clock, FileText,
  Link2, Paperclip, MessageSquare, Save, Trash2,
  CheckCircle, AlertCircle, Edit3, Plus
} from 'lucide-react';
import { Task, User as UserType, TaskStatus, TaskPriority } from '../types/gantt.types';

interface TaskDetailsModalProps {
  task: Task;
  users: UserType[];
  onClose: () => void;
  onUpdate: (task: Task) => void;
  onDelete: () => void;
}

export const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({
  task,
  users,
  onClose,
  onUpdate,
  onDelete
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editedTask, setEditedTask] = useState<Task>(task);
  const [activeTab, setActiveTab] = useState<'general' | 'resources' | 'dependencies' | 'comments'>('general');

  const handleSave = () => {
    onUpdate(editedTask);
    setEditMode(false);
  };

  const handleDelete = () => {
    if (confirm('Czy na pewno chcesz usunąć to zadanie?')) {
      onDelete();
    }
  };

  const statusOptions: { value: TaskStatus; label: string; color: string }[] = [
    { value: 'planned', label: 'Zaplanowane', color: 'text-gray-400' },
    { value: 'in_progress', label: 'W trakcie', color: 'text-blue-400' },
    { value: 'completed', label: 'Ukończone', color: 'text-green-400' },
    { value: 'delayed', label: 'Opóźnione', color: 'text-red-400' },
    { value: 'cancelled', label: 'Anulowane', color: 'text-gray-500' }
  ];

  const priorityOptions: { value: TaskPriority; label: string; color: string }[] = [
    { value: 'low', label: 'Niski', color: 'text-gray-400' },
    { value: 'medium', label: 'Średni', color: 'text-yellow-400' },
    { value: 'high', label: 'Wysoki', color: 'text-orange-400' },
    { value: 'critical', label: 'Krytyczny', color: 'text-red-400' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-zinc-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-zinc-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-zinc-800 to-zinc-900 px-6 py-4 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {editMode ? (
                <input
                  type="text"
                  value={editedTask.name}
                  onChange={(e) => setEditedTask({ ...editedTask, name: e.target.value })}
                  className="text-xl font-bold bg-zinc-800 text-white px-3 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              ) : (
                <h2 className="text-xl font-bold text-white">{task.name}</h2>
              )}
              {task.is_critical_path && (
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-lg">
                  Ścieżka krytyczna
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
                >
                  <Edit3 className="w-5 h-5" />
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Zapisz
                  </button>
                  <button
                    onClick={() => {
                      setEditedTask(task);
                      setEditMode(false);
                    }}
                    className="px-3 py-1.5 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700 transition-colors"
                  >
                    Anuluj
                  </button>
                </>
              )}
              <button
                onClick={handleDelete}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-800">
          <nav className="flex -mb-px">
            {(['general', 'resources', 'dependencies', 'comments'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                {tab === 'general' && 'Ogólne'}
                {tab === 'resources' && 'Zasoby'}
                {tab === 'dependencies' && 'Zależności'}
                {tab === 'comments' && 'Komentarze'}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'general' && (
            <div className="space-y-6">
              {/* Status and Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Status</label>
                  {editMode ? (
                    <select
                      value={editedTask.status}
                      onChange={(e) => setEditedTask({ ...editedTask, status: e.target.value as TaskStatus })}
                      className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      {task.status === 'completed' && <CheckCircle className="w-4 h-4 text-green-400" />}
                      {task.status === 'delayed' && <AlertCircle className="w-4 h-4 text-red-400" />}
                      <span className={statusOptions.find(s => s.value === task.status)?.color}>
                        {statusOptions.find(s => s.value === task.status)?.label}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Priorytet</label>
                  {editMode ? (
                    <select
                      value={editedTask.priority}
                      onChange={(e) => setEditedTask({ ...editedTask, priority: e.target.value as TaskPriority })}
                      className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {priorityOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Flag className={`w-4 h-4 ${priorityOptions.find(p => p.value === task.priority)?.color}`} />
                      <span className={priorityOptions.find(p => p.value === task.priority)?.color}>
                        {priorityOptions.find(p => p.value === task.priority)?.label}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Data rozpoczęcia</label>
                  {editMode ? (
                    <input
                      type="date"
                      value={editedTask.start_date.toISOString().split('T')[0]}
                      onChange={(e) => setEditedTask({ ...editedTask, start_date: new Date(e.target.value) })}
                      className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <div className="flex items-center gap-2 text-white">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {task.start_date.toLocaleDateString('pl-PL')}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Data zakończenia</label>
                  {editMode ? (
                    <input
                      type="date"
                      value={editedTask.end_date.toISOString().split('T')[0]}
                      onChange={(e) => setEditedTask({ ...editedTask, end_date: new Date(e.target.value) })}
                      className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  ) : (
                    <div className="flex items-center gap-2 text-white">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {task.end_date.toLocaleDateString('pl-PL')}
                    </div>
                  )}
                </div>
              </div>

              {/* Progress */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  Postęp: {editMode ? editedTask.progress : task.progress}%
                </label>
                {editMode ? (
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={editedTask.progress}
                    onChange={(e) => setEditedTask({ ...editedTask, progress: parseInt(e.target.value) })}
                    className="w-full"
                  />
                ) : (
                  <div className="w-full bg-zinc-800 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full transition-all"
                      style={{ width: `${task.progress}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Opis</label>
                {editMode ? (
                  <textarea
                    value={editedTask.description || ''}
                    onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 bg-zinc-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Dodaj opis zadania..."
                  />
                ) : (
                  <p className="text-gray-300">
                    {task.description || <span className="text-gray-500">Brak opisu</span>}
                  </p>
                )}
              </div>

              {/* Additional info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Czas trwania</label>
                  <div className="flex items-center gap-2 text-white">
                    <Clock className="w-4 h-4 text-gray-400" />
                    {task.duration} dni
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Szacowany czas</label>
                  <div className="flex items-center gap-2 text-white">
                    <Clock className="w-4 h-4 text-gray-400" />
                    {task.estimated_hours || 0} godzin
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'resources' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Przypisani użytkownicy</h3>
                {editMode && (
                  <button className="px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Dodaj osobę
                  </button>
                )}
              </div>
              
              {task.assigned_users && task.assigned_users.length > 0 ? (
                <div className="space-y-2">
                  {task.assigned_users.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold">
                          {user.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-xs text-gray-400">{user.role}</p>
                        </div>
                      </div>
                      {editMode && (
                        <button className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Brak przypisanych użytkowników</p>
              )}
            </div>
          )}

          {activeTab === 'dependencies' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Zależności</h3>
                {editMode && (
                  <button className="px-3 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Dodaj zależność
                  </button>
                )}
              </div>
              
              {task.dependencies && task.dependencies.length > 0 ? (
                <div className="space-y-2">
                  {task.dependencies.map(dep => (
                    <div key={dep.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Link2 className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-white">Zadanie #{dep.dependent_task_id}</p>
                          <p className="text-xs text-gray-400">Typ: {dep.type}</p>
                        </div>
                      </div>
                      {editMode && (
                        <button className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Brak zależności</p>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Komentarze</h3>
              </div>
              <p className="text-gray-500 text-center py-8">Brak komentarzy</p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};